/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {from, Observable, switchMap} from 'rxjs';

import {environment} from '../environments/environment';
import {AUTHENTICATION_PROVIDER} from './authentication/authentication-provider.token';

/**
 * Adds bearer tokens only to backend requests whose URLs are configured in
 * {@link environment.backend}. Every protected backend service must expose its
 * URL through that configuration; requests to unconfigured or relative URLs
 * are forwarded without an authorization header.
 *
 * Token retrieval failures are not handled here: the authentication provider
 * publishes them on its `error$` stream, where the application-level
 * `AuthenticationErrorHandlerService` reacts to them, and the failure is
 * propagated to the caller of the HTTP request.
 */
@Injectable()
export class CustomAuthHttpInterceptor implements HttpInterceptor {
  private readonly authenticationProvider = inject(AUTHENTICATION_PROVIDER);
  private readonly protectedBackendUrlPrefixes = Object.values(
    environment.backend,
  ).map((backendHost) => `${backendHost}/`);
  private readonly publicPaths = [
    '/passport-management-service/passports/public/',
    '/passport-management-service/definitions/schema/',
    '/aas-connector/api/aas/dpp/public/',
  ];

  // Method-specific public endpoints: only bypass token injection for the exact HTTP method
  private readonly publicMethodPatterns: Array<{
    method: string;
    pattern: RegExp;
  }> = [
    {
      // Public POST: external users submit an access request (no auth required)
      method: 'POST',
      pattern: /\/user-management-service\/organizations\/[^/]+\/requests$/,
    },
    {
      // Public POST: email verification link handler (no auth required)
      method: 'POST',
      pattern:
        /\/user-management-service\/organizations\/[^/]+\/requests\/[^/]+\/verify$/,
    },
  ];

  /**
   * Adds a valid bearer token to protected Battery Passport backend requests.
   * Protected backend calls must use a URL configured in
   * {@link environment.backend}; host or path matching alone does not make a
   * request protected. A token retrieval failure aborts the request and is
   * rethrown to the caller without forwarding the request.
   *
   * @param request - The outbound HTTP request.
   * @param next - The handler for the remaining HTTP interceptor chain.
   * @returns The HTTP event stream for the outgoing request.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const targetUrl = new URL(request.url, window.location.origin);
    const isProtectedBackendRequest = this.protectedBackendUrlPrefixes.some(
      (urlPrefix) => request.url.startsWith(urlPrefix),
    );

    const isPublicPath =
      this.publicPaths.some((path) => targetUrl.pathname.startsWith(path)) ||
      this.publicMethodPatterns.some(
        ({method, pattern}) =>
          request.method === method && pattern.test(targetUrl.pathname),
      );

    if (isProtectedBackendRequest && !isPublicPath) {
      return from(this.authenticationProvider.getAccessToken()).pipe(
        switchMap((accessToken) =>
          next.handle(
            request.clone({
              setHeaders: {Authorization: `Bearer ${accessToken}`},
            }),
          ),
        ),
      );
    }

    return next.handle(request);
  }
}
